import { faCaretDown, faCaretUp, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { Avatar, Pagination, StyledFlex, Table, Tooltip } from '@tryhackme/thm-ui-components';
import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useGetScoreboardDataQuery } from 'src/features/room/room.slice';
import { RankTitle } from './components';
import { useAccordion } from './components/grouped-scoreboard/use-scoreboard-accordion';
import { StyledAccordionIcon, StyledCell, StyledCheckIcon, StyledCheckIconWrapper, StyledDisabledCheckIcon, StyledInfoBox, StyledPaginationContainer, StyledRecordDetailText, StyledRow, StyledTableWrapper, StyledTeamname, StyledUsername, StyledUsernameSecondRowWrapper, StyledUsernameWrapper, SubTableStyledCell, TeamStyledRow } from './scoreboard.styles';
import type { Answerer, PaginationType, ScoreboardDataWithPagination, ScoreboardProps, ScoreboardRow, TaskProgress } from './scoreboard.types';
const TeamRow = memo<{
  answerer: Answerer;
  sb: ScoreboardRow & {
    tasks: TaskProgress[];
  };
  isOpen: boolean;
}>(({
  answerer,
  sb,
  isOpen
}) => <TeamStyledRow key={`${sb?.rank || 'unknown'}-${answerer?._id || 'unknown'}`} role="row" isOpen={isOpen}>
      <StyledCell role="cell" />
      <SubTableStyledCell>
        <StyledFlex justifyContent="flex-start">
          <Avatar hasBorder={false} avatarSrc={answerer?.avatar || ''} size="24px" />
          <StyledUsernameWrapper>
            <StyledUsername href={answerer?.username ? `https://tryhackme.com/p/${answerer.username}` : '#'}>
              {answerer?.username || 'Unknown User'}
            </StyledUsername>
          </StyledUsernameWrapper>
        </StyledFlex>
      </SubTableStyledCell>
      {sb?.tasks && Array.isArray(sb.tasks) ? sb.tasks.map(task => {
    const taskNo = task?.taskNo;
    const taskAnsweredBy = task?.answeredBy;
    return <StyledCell key={`${taskNo || 'unknown'}-${answerer?.username || 'unknown'}`}>
                {taskAnsweredBy?.username && answerer?.username && taskAnsweredBy.username === answerer.username && <StyledCheckIcon data-testid="answered" icon={faCircleCheck} />}
              </StyledCell>;
  }) : null}
      <StyledCell />
    </TeamStyledRow>);
const ScoreboardItem = memo<{
  sb: ScoreboardRow & {
    tasks: TaskProgress[];
  };
  isTeamRoom: boolean;
}>(({
  sb,
  isTeamRoom
}) => {
  const {
    isOpen,
    handleClick
  } = useAccordion(false);
  const answerers = useMemo(() => {
    const set = new Set<string>();
    for (const task of sb.tasks) {
      if (task.answeredBy?.username && !set.has(task.answeredBy.username)) {
        set.add(task.answeredBy.username);
      }
    }
    return [...set].map(username => {
      const answerer: Partial<Answerer> = sb.tasks.find(task => task.answeredBy?.username === username)?.answeredBy || {};
      return {
        username,
        avatar: answerer?.avatar || '',
        _id: answerer?._id || ''
      };
    });
  }, [sb.tasks]);
  return <>
      <StyledRow key={`${sb.rank}-main`} role="row" onClick={handleClick}>
        <StyledCell role="cell" $isCurrentTeam={!!sb.isCurrentTeam}>
          {sb.rank}
        </StyledCell>
        <StyledCell>
          <StyledFlex justifyContent="flex-start">
            {answerers.length > 0 && isTeamRoom ? <StyledAccordionIcon data-testid="accordion-icon" icon={isOpen ? faCaretUp : faCaretDown} /> : null}
            <Avatar hasBorder={false} avatarSrc={sb.avatar} size="32px" isSquare={isTeamRoom} />
            <StyledUsernameWrapper>
              {isTeamRoom ? <StyledTeamname $isCurrentTeam={!!sb.isCurrentTeam}>{sb.username}</StyledTeamname> : <StyledUsername href={`https://tryhackme.com/p/${sb.username}`}>{sb.username}</StyledUsername>}
              <StyledUsernameSecondRowWrapper>
                <RankTitle level={sb.level} />
              </StyledUsernameSecondRowWrapper>
            </StyledUsernameWrapper>
          </StyledFlex>
        </StyledCell>
        {sb.tasks.map(({
        correct,
        _id,
        score,
        taskNo,
        questionNo
      }) => <StyledCell key={`task-${taskNo}-${questionNo}-${_id}`}>
            {correct ? <StyledCheckIconWrapper>
                <Tooltip arrowPosition="bottom-center">{`${score} points`}</Tooltip>
                <StyledCheckIcon data-testid="checked" icon={faCircleCheck} />
              </StyledCheckIconWrapper> : <StyledDisabledCheckIcon data-testid="unchecked" icon={faCircleCheck} />}
          </StyledCell>)}
        <StyledCell>{sb.score}</StyledCell>
      </StyledRow>

      {isOpen && answerers.map(answerer => {
      if (!answerer?.username) return null;
      return <TeamRow key={`${sb.rank}-${answerer._id}`} answerer={answerer} sb={sb} isOpen={isOpen} />;
    })}
    </>;
});
export const ScoreBoard: FC<ScoreboardProps> = ({
  scoreboardDetails: initialScoreboardData = [],
  roomDetails,
  isLoading,
  searchTerm
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    limit: 10,
    page: 1,
    totalPages: 0,
    pagingCounter: 1,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null
  });
  const isTeamRoom = roomDetails?.teams ?? false;
  const {
    data,
    isFetching: isScoreboardFetching
  } = useGetScoreboardDataQuery({
    roomCode: roomDetails?.code ?? '',
    limit: pagination.limit,
    page: currentPage,
    searchTerm
  }, {
    skip: !roomDetails
  });
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(searchTerm && (pagination.hasNextPage || pagination.hasPrevPage) ? 1 : currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);
  const scoreboardData = data?.data as unknown as ScoreboardDataWithPagination;
  const roomScoreboardDetails = isTeamRoom ? scoreboardData?.docs : initialScoreboardData;
  const [scoreboardDetails, setScoreboardDetails] = useState<ScoreboardProps['scoreboardDetails']>(roomScoreboardDetails);
  useEffect(() => {
    if (scoreboardData?.pagination) {
      setPagination(scoreboardData.pagination);
    }
    if (scoreboardData?.docs) {
      setScoreboardDetails(scoreboardData.docs);
    }
  }, [scoreboardData]);
  const {
    scoreboardHeaderNames,
    scoreboardHeaderSpacing,
    scoreboardRows
  } = useMemo(() => {
    if (!scoreboardDetails?.length) {
      return {
        scoreboardHeaderData: [],
        scoreboardHeaderNames: [],
        scoreboardHeaderSpacing: '',
        scoreboardRows: []
      };
    }
    let entryWithMostTasks = scoreboardDetails[0];
    let maxTaskCount = Object.values(entryWithMostTasks?.tasks || {}).flat().length;
    for (const entry of scoreboardDetails) {
      const currentTaskCount = Object.values(entry?.tasks || {}).flat().length;
      if (currentTaskCount > maxTaskCount) {
        maxTaskCount = currentTaskCount;
        entryWithMostTasks = entry;
      }
    }
    const headerData = Object.entries(entryWithMostTasks?.tasks || {}).flatMap(([taskNo, questions]) => (questions as TaskProgress[]).map(question => ({
      questionNo: question.questionNo,
      taskNo: Number.parseInt(taskNo, 10)
    }))).sort((a, b) => a.taskNo === b.taskNo ? a.questionNo - b.questionNo : a.taskNo - b.taskNo);
    const headerNames = ['Rank', 'Username', ...headerData.map(task => `[Task ${task.taskNo}] #${task.questionNo}`), 'Score'];
    const headerSpacing = ['minMax(7.06rem,7.06rem)', `minMax(40rem,1fr)`, ...headerData.map(() => `minMax(10.3rem,10.3rem)`), 'minMax(10.3rem,10.3rem)'].join(' ');
    const rows = scoreboardDetails.map(row => {
      const existingTasks = Object.entries(row.tasks).flatMap(([taskNo, questions]) => (questions as TaskProgress[]).map(task => ({
        ...task,
        taskId: task._id,
        taskNo,
        answeredBy: task.answeredBy || null
      })));
      const taskMap = new Map<string, TaskProgress & {
        taskId: string;
        taskNo: string;
      }>();
      for (const task of existingTasks) {
        const key = `${task.taskNo}-${task.questionNo}`;
        taskMap.set(key, task);
      }
      const alignedTasks = headerData.map(headerTask => {
        const key = `${headerTask.taskNo}-${headerTask.questionNo}`;
        return taskMap.has(key) ? taskMap.get(key)! : {
          correct: false,
          _id: `placeholder-${key}`,
          taskId: `placeholder-${key}`,
          score: 0,
          taskNo: String(headerTask.taskNo),
          questionNo: headerTask.questionNo,
          answeredBy: null
        } as TaskProgress & {
          taskId: string;
          taskNo: string;
        };
      });
      return {
        ...row,
        tasks: alignedTasks
      };
    });
    return {
      scoreboardHeaderData: headerData,
      scoreboardHeaderNames: headerNames,
      scoreboardHeaderSpacing: headerSpacing,
      scoreboardRows: rows
    };
  }, [scoreboardDetails]);
  const noData = !scoreboardDetails?.length || scoreboardDetails.every(row => !row.tasks || Object.keys(row.tasks).length === 0);
  if (noData && !isLoading && !isScoreboardFetching) return <StyledInfoBox>There are no scores to show yet.</StyledInfoBox>;
  const totalDocs = pagination.total;
  const {
    totalPages,
    hasNextPage,
    limit
  } = pagination;
  const endOffset = currentPage * limit;
  const startOffset = endOffset - limit;
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  return <StyledTableWrapper $isModuleScoreboard={false} data-sentry-element="StyledTableWrapper" data-sentry-component="ScoreBoard" data-sentry-source-file="scoreboard.tsx">
      <Table isLoading={isLoading || isScoreboardFetching} dataHead={scoreboardHeaderNames} columns={scoreboardHeaderSpacing} columnsMobile={scoreboardHeaderSpacing} alignToLeftColumns={[1, 2]} data-sentry-element="Table" data-sentry-source-file="scoreboard.tsx">
        {scoreboardRows.map(sb => {
        if (!sb.tasks?.length) return null;
        return <ScoreboardItem key={`scoreboard-${sb.rank}`} sb={sb} isTeamRoom={isTeamRoom} />;
      })}
      </Table>

      {totalPages > 1 && <StyledPaginationContainer>
          <StyledRecordDetailText>
            {`Showing ${startOffset + 1} to ${hasNextPage ? endOffset : totalDocs} of ${String(totalDocs)} records`}
          </StyledRecordDetailText>
          <div>
            <Pagination totalPages={totalPages} selectedPage={currentPage} onChange={handlePageChange} />
          </div>
        </StyledPaginationContainer>}
    </StyledTableWrapper>;
};